import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faPlus,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faMobile,
    faExclamationCircle,
    faExclamationTriangle,
    faCalendarAlt,
    faClock,
    faTag,
    faFileInvoice,
    faBuilding,
    faUser,
    faPhone,
    faCreditCard,
    faReceipt,
    faImage,
    faTrash,
    faEdit,
    faEye,
    faFilter,
    faChartLine,
    faChartBar,
    faChartPie,
    faDownload,
    faPrint,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faBoxes,
    faStore,
    faPercentage,
    faComment,
    faCheck,
    faBan,
    faMoneyCheck,
    faHistory,
    faExchangeAlt,
    faIdCard,
    faBarcode,
    faUniversity,
    faDollarSign,
    faMapMarkerAlt,
    faList,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createExpenseTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_expense_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Expense Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_expense_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newExpense: 'New Expense',
        editExpense: 'Edit Expense',
        expenseDetails: 'Expense Details',
        expenseNo: 'Expense No',
        expenseDate: 'Expense Date',
        expenseTime: 'Expense Time',
        category: 'Category',
        subCategory: 'Sub Category',
        title: 'Title',
        description: 'Description',
        amount: 'Amount',
        taxRate: 'Tax Rate (%)',
        tax: 'Tax',
        totalAmount: 'Total Amount',
        paymentMethod: 'Payment Method',
        paymentReference: 'Payment Reference',
        vendorName: 'Vendor Name',
        vendorContact: 'Vendor Contact',
        billNumber: 'Bill/Invoice Number',
        billImage: 'Bill Image',
        status: 'Status',
        remarks: 'Remarks',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        creditCard: 'Credit Card',
        debitCard: 'Debit Card',
        cheque: 'Cheque',
        upi: 'UPI',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        paid: 'Paid',
        save: 'Save Expense',
        update: 'Update Expense',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        history: 'History',
        changeStatus: 'Change Status',
        expenseAdded: 'Expense added successfully!',
        expenseUpdated: 'Expense updated successfully!',
        expenseDeleted: 'Expense deleted successfully!',
        failedSave: 'Failed to save expense',
        failedUpdate: 'Failed to update expense',
        failedDelete: 'Failed to delete expense',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        titleRequired: 'Title is required',
        amountRequired: 'Amount is required and must be positive',
        categoryRequired: 'Category is required',
        paymentMethodRequired: 'Payment method is required',
        dateRequired: 'Date is required',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalExpenses: 'Total Expenses',
        totalAmount: 'Total Amount',
        averageExpense: 'Average Expense',
        totalTax: 'Total Tax',
        expensesByCategory: 'Expenses by Category',
        recentExpenses: 'Recent Expenses',
        addNew: 'Add New Expense',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Are you sure you want to delete this expense?',
        cancel: 'Cancel',
        ok: 'OK',
        searchPlaceholder: 'Search by title, expense no, vendor...',
        filterByCategory: 'Filter by Category',
        filterByStatus: 'Filter by Status',
        filterByDate: 'Filter by Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        loadingData: 'Loading expenses...',
        noExpenses: 'No expenses found',
        poweredBy: 'Powered by OurZest',
        footer: '© auto year ourzest pvt ltd',
        expenseInformation: 'Expense Information',
        vendorDetails: 'Vendor Details',
        paymentDetails: 'Payment Details',
        uploadBillImage: 'Upload Bill Image',
        selectCategory: 'Select Category',
        selectSubCategory: 'Select Sub Category',
        selectStatus: 'Select Status',
        selectPaymentMethod: 'Select Payment Method',
        currentStatus: 'Current Status',
        newStatus: 'New Status',
        reasonForChange: 'Reason for change',
        additionalNotes: 'Additional Notes',
        updateStatus: 'Update Status',
        updating: 'Updating...',
        statusUpdated: 'Status updated successfully!',
        expenseHistory: 'Expense History',
        showList: 'Show List',
        hideList: 'Hide List',
        actions: 'Actions'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const expenseTranslator = createExpenseTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{ 
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img 
                    src={businessInfo.bussinesslogo} 
                    alt="Logo" 
                    style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Main Expense Component
// ==============================================
const PosExpensesForm = ({ expenseId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [formData, setFormData] = useState({
        expense_date: new Date().toISOString().split('T')[0],
        expense_time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
        category: '',
        sub_category: '',
        title: '',
        description: '',
        amount: '',
        tax_rate: '0',
        payment_method: 'cash',
        payment_reference: '',
        vendor_name: '',
        vendor_contact: '',
        bill_number: '',
        status: 'pending',
        remarks: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    // List states
    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    const [billImage, setBillImage] = useState(null);
    const [billImagePreview, setBillImagePreview] = useState('');
    const [showList, setShowList] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        category: '',
        status: '',
        payment_method: '',
        search: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0
    });
    
    // Modal states
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);

    // Refs
    const toastTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Payment methods
    const paymentMethods = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'credit_card', label: 'Credit Card', icon: faCreditCard },
        { value: 'debit_card', label: 'Debit Card', icon: faCreditCard },
        { value: 'cheque', label: 'Cheque', icon: faMoneyCheck },
        { value: 'upi', label: 'UPI', icon: faMobile }
    ];

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending', icon: faClock },
        { value: 'approved', label: 'Approved', icon: faCheckCircle },
        { value: 'rejected', label: 'Rejected', icon: faBan },
        { value: 'paid', label: 'Paid', icon: faMoneyBillWave }
    ];

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await expenseTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        expenseTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            expenseTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Load categories
    const loadCategories = useCallback(async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_categories',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setCategories(data.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    }, [shopId]);

    // Load expenses list
    const loadExpenses = useCallback(async () => {
        if (!shopId) return;

        setLoading(true);
        try {
            const payload = {
                action: 'get_expenses',
                shop_id: shopId,
                page: pagination.page,
                limit: pagination.limit,
                sort: 'expense_date',
                order: 'DESC'
            };
            
            if (filters.date_from) payload.filters = { ...payload.filters, date_from: filters.date_from };
            if (filters.date_to) payload.filters = { ...payload.filters, date_to: filters.date_to };
            if (filters.category) payload.filters = { ...payload.filters, category: filters.category };
            if (filters.status) payload.filters = { ...payload.filters, status: filters.status };
            if (filters.payment_method) payload.filters = { ...payload.filters, payment_method: filters.payment_method };
            if (filters.search) payload.filters = { ...payload.filters, search: filters.search };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                setExpenses(data.data);
                setPagination(prev => ({ ...prev, total: data.total }));
                setSummary(data.summary);
            }
        } catch (error) {
            console.error('Error loading expenses:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [shopId, pagination.page, pagination.limit, filters, showToast, translations]);

    // Load expense data if in edit mode
    useEffect(() => {
        if (expenseId) {
            setIsEditMode(true);
            loadExpenseData(expenseId);
        }
    }, [expenseId]);

    // Load data on mount
    useEffect(() => {
        loadBusinessInfo();
        loadCategories();
        loadExpenses();
    }, [shopId, loadBusinessInfo, loadCategories, loadExpenses]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
            setShopId(currentShop.shop_id);
        }
    }, [currentShop]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    const loadExpenseData = async (id) => {
        if (!shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_expense_by_id',
                    id: id,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const expense = data.data;
                setFormData(prev => ({
                    ...prev,
                    expense_date: expense.expense_date || new Date().toISOString().split('T')[0],
                    expense_time: expense.expense_time || new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
                    category: expense.category || '',
                    sub_category: expense.sub_category || '',
                    title: expense.title || '',
                    description: expense.description || '',
                    amount: expense.amount || '',
                    tax_rate: expense.tax_rate || '0',
                    payment_method: expense.payment_method || 'cash',
                    payment_reference: expense.payment_reference || '',
                    vendor_name: expense.vendor_name || '',
                    vendor_contact: expense.vendor_contact || '',
                    bill_number: expense.bill_number || '',
                    status: expense.status || 'pending',
                    remarks: expense.remarks || ''
                }));

                // Set subcategories based on selected category
                const selectedCategory = categories.find(c => c.category_name === expense.category);
                if (selectedCategory && selectedCategory.sub_categories) {
                    try {
                        const subCats = JSON.parse(selectedCategory.sub_categories);
                        setSubCategories(subCats);
                    } catch (e) {
                        setSubCategories([]);
                    }
                }

                if (expense.bill_image) {
                    setBillImagePreview(expense.bill_image);
                }
            } else {
                showToast('error', data.message || translations?.failedLoad || 'Failed to load expense data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Handle category change
    const handleCategoryChange = (e) => {
        const categoryValue = e.target.value;
        const selectedCategory = categories.find(c => c.category_name === categoryValue);
        
        let subCats = [];
        if (selectedCategory && selectedCategory.sub_categories) {
            try {
                subCats = JSON.parse(selectedCategory.sub_categories);
            } catch (e) {
                subCats = [];
            }
        }
        
        setSubCategories(subCats);
        setFormData(prev => ({ 
            ...prev, 
            category: categoryValue,
            sub_category: ''
        }));
        
        if (errors.category) {
            setErrors(prev => ({ ...prev, category: '' }));
        }
    };

    // Handle bill image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('error', 'Image size should be less than 5MB');
                return;
            }
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setBillImagePreview(reader.result);
                setBillImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Remove bill image
    const removeBillImage = () => {
        setBillImagePreview('');
        setBillImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Calculate total amount with tax
    const calculateTotal = () => {
        const amount = parseFloat(formData.amount) || 0;
        const taxRate = parseFloat(formData.tax_rate) || 0;
        const tax = amount * (taxRate / 100);
        return (amount + tax).toFixed(2);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = translations?.titleRequired || 'Title is required';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = translations?.amountRequired || 'Amount is required and must be positive';
        }

        if (!formData.category) {
            newErrors.category = translations?.categoryRequired || 'Category is required';
        }

        if (!formData.payment_method) {
            newErrors.payment_method = translations?.paymentMethodRequired || 'Payment method is required';
        }

        if (!formData.expense_date) {
            newErrors.expense_date = translations?.dateRequired || 'Date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || localStorage.getItem('username');
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            const action = isEditMode ? 'update_expense' : 'add_expense';

            const payload = {
                action: action,
                ...formData,
                amount: parseFloat(formData.amount),
                tax_rate: parseFloat(formData.tax_rate),
                shop_id: shopId,
                created_by: currentUsername,
                bill_image: billImage
            };

            if (isEditMode && expenseId) {
                payload.id = expenseId;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                const successMessage = isEditMode 
                    ? (translations?.expenseUpdated || 'Expense updated successfully!')
                    : (translations?.expenseAdded || 'Expense added successfully!');
                
                showToast('success', successMessage);

                if (!isEditMode) {
                    handleClear();
                }

                setErrors({});
                loadExpenses(); // Refresh the list

                if (onSuccess) {
                    onSuccess(data.data?.id || expenseId);
                }

            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save expense');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData({
            expense_date: new Date().toISOString().split('T')[0],
            expense_time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
            category: '',
            sub_category: '',
            title: '',
            description: '',
            amount: '',
            tax_rate: '0',
            payment_method: 'cash',
            payment_reference: '',
            vendor_name: '',
            vendor_contact: '',
            bill_number: '',
            status: 'pending',
            remarks: '',
            themeColor: formData.themeColor
        });
        setErrors({});
        setSubCategories([]);
        removeBillImage();
        setIsEditMode(false);
    };

    // Delete expense
    const handleDelete = async (id) => {
        if (!window.confirm(translations?.confirmDelete || 'Are you sure you want to delete this expense?')) return;
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_expense',
                    id: id
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations?.expenseDeleted || 'Expense deleted successfully');
                loadExpenses();
            } else {
                showToast('error', data.message || translations?.failedDelete || 'Failed to delete expense');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        }
    };

    // Edit expense from list
    const handleEditFromList = (expense) => {
        setIsEditMode(true);
        setFormData({
            expense_date: expense.expense_date,
            expense_time: expense.expense_time,
            category: expense.category,
            sub_category: expense.sub_category || '',
            title: expense.title,
            description: expense.description || '',
            amount: expense.amount.toString(),
            tax_rate: expense.tax_rate.toString(),
            payment_method: expense.payment_method,
            payment_reference: expense.payment_reference || '',
            vendor_name: expense.vendor_name || '',
            vendor_contact: expense.vendor_contact || '',
            bill_number: expense.bill_number || '',
            status: expense.status,
            remarks: expense.remarks || '',
            themeColor: formData.themeColor
        });
        
        if (expense.bill_image) {
            setBillImagePreview(expense.bill_image);
        }
        
        // Set subcategories based on selected category
        const selectedCategory = categories.find(c => c.category_name === expense.category);
        if (selectedCategory && selectedCategory.sub_categories) {
            try {
                const subCats = JSON.parse(selectedCategory.sub_categories);
                setSubCategories(subCats);
            } catch (e) {
                setSubCategories([]);
            }
        }
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Update expense status
    const handleStatusUpdate = async (id, status) => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_expenses.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_status',
                    id: id,
                    status: status,
                    approved_by: user?.username
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations?.statusUpdated || 'Status updated successfully!');
                loadExpenses();
            } else {
                showToast('error', data.message || translations?.failedUpdate || 'Failed to update status');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        }
    };

    const handleViewHistory = () => {
        setShowHistoryModal(true);
    };

    const handleChangeStatus = () => {
        setShowStatusModal(true);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const colors = {
            pending: { bg: '#fef3c7', color: '#d97706', text: 'Pending' },
            approved: { bg: '#d1fae5', color: '#059669', text: 'Approved' },
            rejected: { bg: '#fee2e2', color: '#dc2626', text: 'Rejected' },
            paid: { bg: '#e0e7ff', color: '#4f46e5', text: 'Paid' }
        };
        const style = colors[status] || colors.pending;
        return (
            <span style={{
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '500',
                background: style.bg,
                color: style.color,
                display: 'inline-block'
            }}>
                {translations?.[status] || style.text}
            </span>
        );
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faEdit : faMoneyBillWave} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editExpense || 'Edit Expense') : (translations?.newExpense || 'New Expense')}</h1>
                        <p>{translations?.expenseDetails || 'Expense Details'}</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        type="button"
                        className="btn-icon"
                        onClick={() => setShowList(!showList)}
                        title={showList ? (translations?.hideList || 'Hide List') : (translations?.showList || 'Show List')}
                    >
                        <FontAwesomeIcon icon={showList ? faEyeSlash : faList} />
                    </button>
                    {isEditMode && (
                        <>
                            <button
                                type="button"
                                className="btn-icon"
                                onClick={handleViewHistory}
                                title={translations?.history || 'History'}
                            >
                                <FontAwesomeIcon icon={faHistory} />
                            </button>
                            <button
                                type="button"
                                className="btn-icon"
                                onClick={handleChangeStatus}
                                title={translations?.changeStatus || 'Change Status'}
                            >
                                <FontAwesomeIcon icon={faExchangeAlt} />
                            </button>
                        </>
                    )}
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Summary Cards */}
            {summary && !isEditMode && (
                <div className="summary-cards-expenses" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    marginBottom: '25px'
                }}>
                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '10px',
                            background: '#e0e7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4f46e5'
                        }}>
                            <FontAwesomeIcon icon={faReceipt} size="lg" />
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{translations?.totalExpenses || 'Total Expenses'}</div>
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>{summary.total_expenses || 0}</div>
                        </div>
                    </div>
                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '10px',
                            background: '#d1fae5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#10b981'
                        }}>
                            <FontAwesomeIcon icon={faMoneyBillWave} size="lg" />
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{translations?.totalAmount || 'Total Amount'}</div>
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>Rs.{formatCurrency(summary.total_amount || 0)}</div>
                        </div>
                    </div>
                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '10px',
                            background: '#fed7aa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#f97316'
                        }}>
                            <FontAwesomeIcon icon={faChartLine} size="lg" />
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{translations?.averageExpense || 'Average Expense'}</div>
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>Rs.{formatCurrency(summary.avg_amount || 0)}</div>
                        </div>
                    </div>
                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '10px',
                            background: '#fce7f3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ec4899'
                        }}>
                            <FontAwesomeIcon icon={faPercentage} size="lg" />
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{translations?.totalTax || 'Total Tax'}</div>
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>Rs.{formatCurrency(summary.total_tax || 0)}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Expense Information Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faFileInvoice} />
                            {translations?.expenseInformation || 'Expense Information'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Expense Date - Required */}
                        <div className={`form-group-modern ${errors.expense_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="expense_date"
                                    name="expense_date"
                                    value={formData.expense_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                    className={errors.expense_date ? 'error' : ''}
                                />
                                <label htmlFor="expense_date" className="input-label">
                                    {translations?.expenseDate || 'Expense Date'} <span className="required">*</span>
                                </label>
                                {errors.expense_date && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.expense_date}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Expense Time */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faClock} className="input-icon" />
                                <input
                                    type="time"
                                    id="expense_time"
                                    name="expense_time"
                                    value={formData.expense_time}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="expense_time" className="input-label">
                                    {translations?.expenseTime || 'Expense Time'}
                                </label>
                            </div>
                        </div>

                        {/* Category - Required with Sub Categories */}
                        <div className={`form-group-modern ${errors.category ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    disabled={saving}
                                    className={errors.category ? 'error' : ''}
                                >
                                    <option value="">{translations?.selectCategory || 'Select Category'}</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.category_name}>
                                            {cat.category_name}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="category" className="input-label">
                                    {translations?.category || 'Category'} <span className="required">*</span>
                                </label>
                                {errors.category && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.category}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sub Category - Shows only when category has subcategories */}
                        {subCategories.length > 0 && (
                            <div className="form-group-modern">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faBoxes} className="input-icon" />
                                    <select
                                        id="sub_category"
                                        name="sub_category"
                                        value={formData.sub_category}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        <option value="">{translations?.selectSubCategory || 'Select Sub Category'}</option>
                                        {subCategories.map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="sub_category" className="input-label">
                                        {translations?.subCategory || 'Sub Category'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Title - Required */}
                        <div className={`form-group-modern full-width ${errors.title ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    className={errors.title ? 'error' : ''}
                                />
                                <label htmlFor="title" className="input-label">
                                    {translations?.title || 'Title'} <span className="required">*</span>
                                </label>
                                {errors.title && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.title}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="form-group-modern full-width">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faComment} className="input-icon" />
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    rows="3"
                                ></textarea>
                                <label htmlFor="description" className="input-label">
                                    {translations?.description || 'Description'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Amount Section */}
                <div className="form-section">
                    <div className="form-grid-modern">
                        {/* Amount - Required */}
                        <div className={`form-group-modern ${errors.amount ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.01"
                                    id="amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    className={errors.amount ? 'error' : ''}
                                />
                                <label htmlFor="amount" className="input-label">
                                    {translations?.amount || 'Amount (Rs.)'} <span className="required">*</span>
                                </label>
                                {errors.amount && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.amount}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tax Rate */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPercentage} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.01"
                                    id="tax_rate"
                                    name="tax_rate"
                                    value={formData.tax_rate}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                />
                                <label htmlFor="tax_rate" className="input-label">
                                    {translations?.taxRate || 'Tax Rate (%)'}
                                </label>
                            </div>
                        </div>

                        {/* Total Amount */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                <input
                                    type="text"
                                    value={`Rs. ${calculateTotal()}`}
                                    readOnly
                                    className="total-field"
                                    style={{ fontWeight: 'bold', color: 'var(--purchase-primary-color)' }}
                                />
                                <label className="input-label">
                                    {translations?.totalAmount || 'Total Amount (Rs.)'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Details Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faCreditCard} />
                            {translations?.paymentDetails || 'Payment Details'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Payment Method - Required */}
                        <div className={`form-group-modern ${errors.payment_method ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                                <select
                                    id="payment_method"
                                    name="payment_method"
                                    value={formData.payment_method}
                                    onChange={handleChange}
                                    disabled={saving}
                                    className={errors.payment_method ? 'error' : ''}
                                >
                                    <option value="">{translations?.selectPaymentMethod || 'Select Payment Method'}</option>
                                    {paymentMethods.map(method => (
                                        <option key={method.value} value={method.value}>
                                            {translations?.[method.label.toLowerCase().replace(' ', '')] || method.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="payment_method" className="input-label">
                                    {translations?.paymentMethod || 'Payment Method'} <span className="required">*</span>
                                </label>
                                {errors.payment_method && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.payment_method}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Reference */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBarcode} className="input-icon" />
                                <input
                                    type="text"
                                    id="payment_reference"
                                    name="payment_reference"
                                    value={formData.payment_reference}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                />
                                <label htmlFor="payment_reference" className="input-label">
                                    {translations?.paymentReference || 'Payment Reference (UTR/ID)'}
                                </label>
                            </div>
                        </div>

                        {/* Bill/Invoice Number */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                <input
                                    type="text"
                                    id="bill_number"
                                    name="bill_number"
                                    value={formData.bill_number}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                />
                                <label htmlFor="bill_number" className="input-label">
                                    {translations?.billNumber || 'Bill/Invoice Number'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vendor Details Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faBuilding} />
                            {translations?.vendorDetails || 'Vendor Details'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Vendor Name */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="vendor_name"
                                    name="vendor_name"
                                    value={formData.vendor_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                />
                                <label htmlFor="vendor_name" className="input-label">
                                    {translations?.vendorName || 'Vendor Name'}
                                </label>
                            </div>
                        </div>

                        {/* Vendor Contact */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="text"
                                    id="vendor_contact"
                                    name="vendor_contact"
                                    value={formData.vendor_contact}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                />
                                <label htmlFor="vendor_contact" className="input-label">
                                    {translations?.vendorContact || 'Vendor Contact'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bill Image Section */}
                <div className="form-section">
                    <div className="form-group-modern">
                        <div className="image-upload-area">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                            {billImagePreview ? (
                                <div className="image-preview">
                                    <img src={billImagePreview} alt="Bill preview" />
                                    <button type="button" onClick={removeBillImage} className="remove-image">
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            ) : (
                                <button type="button" onClick={() => fileInputRef.current?.click()} className="upload-btn">
                                    <FontAwesomeIcon icon={faImage} />
                                    {translations?.uploadBillImage || 'Upload Bill Image'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status & Remarks Section */}
                <div className="form-section">
                    <div className="form-grid-modern">
                        {/* Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faEye} className="input-icon" />
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    disabled={saving || isEditMode}
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {translations?.[opt.label.toLowerCase()] || opt.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations?.status || 'Status'}
                                </label>
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="form-group-modern full-width">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faComment} className="input-icon" />
                                <textarea
                                    id="remarks"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    rows="2"
                                ></textarea>
                                <label htmlFor="remarks" className="input-label">
                                    {translations?.remarks || 'Remarks'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.update || 'Update Expense') : (translations?.save || 'Save Expense')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Expense History List Section - Collapsible */}
            {showList && (
                <div className="form-section" style={{ marginTop: '30px' }}>
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faHistory} />
                            {translations?.expenseHistory || 'Expense History'}
                        </h3>
                        <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
                            <FontAwesomeIcon icon={faFilter} />
                            {translations?.filters || 'Filters'}
                        </button>
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div className="filters-panel" style={{
                            display: 'flex',
                            gap: '15px',
                            flexWrap: 'wrap',
                            padding: '15px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.startDate || 'Date From'}</label>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.endDate || 'Date To'}</label>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.category || 'Category'}</label>
                                <select
                                    value={filters.category}
                                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="">{translations?.allDepartments || 'All Categories'}</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.status || 'Status'}</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="">{translations?.allStatus || 'All Status'}</option>
                                    {statusOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{translations?.[opt.label.toLowerCase()] || opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.paymentMethod || 'Payment Method'}</label>
                                <select
                                    value={filters.payment_method}
                                    onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="">{translations?.allMethods || 'All Methods'}</option>
                                    {paymentMethods.map(method => (
                                        <option key={method.value} value={method.value}>{translations?.[method.label.toLowerCase().replace(' ', '')] || method.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group" style={{ flex: 2, minWidth: '200px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.search || 'Search'}</label>
                                <input
                                    type="text"
                                    placeholder={translations?.searchPlaceholder || 'Search by title, vendor...'}
                                    value={filters.search}
                                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className="filter-actions" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                <button className="btn-apply" onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); loadExpenses(); }} style={{
                                    padding: '8px 16px',
                                    background: '#4f46e5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}>
                                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '5px' }} />
                                    {translations?.applyFilters || 'Apply'}
                                </button>
                                <button className="btn-reset" onClick={() => {
                                    setFilters({ date_from: '', date_to: '', category: '', status: '', payment_method: '', search: '' });
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                    loadExpenses();
                                }} style={{
                                    padding: '8px 16px',
                                    background: '#e2e8f0',
                                    color: '#475569',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}>
                                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                                    {translations?.resetFilters || 'Reset'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Expenses Table */}
                    {loading ? (
                        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: '#4f46e5' }} />
                            <p>{translations?.loading || 'Loading...'}</p>
                        </div>
                    ) : expenses.length === 0 ? (
                        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                            <FontAwesomeIcon icon={faReceipt} style={{ fontSize: '48px', marginBottom: '10px' }} />
                            <p>{translations?.noExpenses || 'No expenses found'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                                <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.expenseNo || 'Expense No'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.expenseDate || 'Date'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.title || 'Title'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.category || 'Category'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.amount || 'Amount'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.totalAmount || 'Total'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.paymentMethod || 'Payment'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.status || 'Status'}</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenses.map(expense => (
                                            <tr key={expense.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#4f46e5' }}>{expense.expense_no}</td>
                                                <td style={{ padding: '12px' }}>{formatDate(expense.expense_date)}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <strong>{expense.title}</strong>
                                                    {expense.vendor_name && (
                                                        <small style={{ display: 'block', fontSize: '11px', color: '#64748b' }}>{expense.vendor_name}</small>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px' }}>{expense.category}</td>
                                                <td style={{ padding: '12px' }}>Rs.{formatCurrency(expense.amount)}</td>
                                                <td style={{ padding: '12px', fontWeight: '600' }}>Rs.{formatCurrency(expense.total_amount)}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{ padding: '4px 8px', background: '#f1f5f9', borderRadius: '4px', fontSize: '11px' }}>
                                                        {paymentMethods.find(m => m.value === expense.payment_method)?.label || expense.payment_method}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px' }}>{getStatusBadge(expense.status)}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <button
                                                            onClick={() => handleEditFromList(expense)}
                                                            title={translations?.edit || 'Edit'}
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                borderRadius: '6px',
                                                                border: 'none',
                                                                background: '#e0e7ff',
                                                                color: '#4f46e5',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(expense.id)}
                                                            title={translations?.delete || 'Delete'}
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                borderRadius: '6px',
                                                                border: 'none',
                                                                background: '#fee2e2',
                                                                color: '#dc2626',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                        {expense.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleStatusUpdate(expense.id, 'approved')}
                                                                    title={translations?.approve || 'Approve'}
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        borderRadius: '6px',
                                                                        border: 'none',
                                                                        background: '#d1fae5',
                                                                        color: '#10b981',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleStatusUpdate(expense.id, 'rejected')}
                                                                    title={translations?.reject || 'Reject'}
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        borderRadius: '6px',
                                                                        border: 'none',
                                                                        background: '#fee2e2',
                                                                        color: '#dc2626',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faBan} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {expense.status === 'approved' && (
                                                            <button
                                                                onClick={() => handleStatusUpdate(expense.id, 'paid')}
                                                                title={translations?.markAsPaid || 'Mark as Paid'}
                                                                style={{
                                                                    width: '30px',
                                                                    height: '30px',
                                                                    borderRadius: '6px',
                                                                    border: 'none',
                                                                    background: '#d1fae5',
                                                                    color: '#10b981',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                                            </button>
                                                        )}
                                                    </div>
                                                 </td>
                                             </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.total > pagination.limit && (
                                <div className="pagination" style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    marginTop: '20px',
                                    alignItems: 'center'
                                }}>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            background: 'white',
                                            cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                            opacity: pagination.page === 1 ? 0.5 : 1
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                        Previous
                                    </button>
                                    <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            background: 'white',
                                            cursor: pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 'not-allowed' : 'pointer',
                                            opacity: pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 0.5 : 1
                                        }}
                                    >
                                        Next
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="loading-content" style={{
                        background: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        textAlign: 'center'
                    }}>
                        <div className="loading-spinner" style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #e2e8f0',
                            borderTopColor: '#4f46e5',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 15px'
                        }}></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                .filter-toggle {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 13px;
                }
                
                .filter-toggle:hover {
                    background: #e2e8f0;
                }
                
                .filters-panel {
                    animation: fadeIn 0.3s ease;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .btn-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }
                
                .btn-icon:hover {
                    background: #f8fafc;
                    border-color: var(--purchase-primary-color, #8b5cf6);
                    color: var(--purchase-primary-color, #8b5cf6);
                }
                
                .header-actions {
                    display: flex;
                    gap: 10px;
                }
            `}</style>
        </div>
    );
};

export default PosExpensesForm;